// E-Rechnung (X13): gemeinsame Bausteine für Kunden-, Auftrags- und Firmenmaske
import { useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// Länder für Auswahlfelder: ISO-Code und deutscher Name (Name wird zusätzlich als Text gespeichert)
export const COUNTRIES = [
  ['DE', 'Deutschland'],
  ['AT', 'Österreich'],
  ['CH', 'Schweiz'],
  ['NL', 'Niederlande'],
  ['BE', 'Belgien'],
  ['LU', 'Luxemburg'],
  ['FR', 'Frankreich'],
  ['DK', 'Dänemark'],
  ['PL', 'Polen'],
  ['CZ', 'Tschechien'],
  ['NO', 'Norwegen'],
  ['SE', 'Schweden'],
  ['FI', 'Finnland'],
  ['GB', 'Vereinigtes Königreich'],
  ['IE', 'Irland'],
  ['IT', 'Italien'],
  ['ES', 'Spanien'],
  ['PT', 'Portugal'],
  ['EE', 'Estland'],
  ['LV', 'Lettland'],
  ['LT', 'Litauen'],
  ['US', 'Vereinigte Staaten'],
];

export const countryName = (code) => (COUNTRIES.find(([c]) => c === code) || [code, code])[1];

// Steuerkategorien, die CTIme anbietet (K und O bewusst nicht, siehe X12)
export const TAX_CATEGORIES = [
  ['S', 'Regelsatz (S)'],
  ['AE', 'Reverse Charge (AE) – § 13b / EU-Unternehmer'],
  ['G', 'Ausfuhr Drittland (G)'],
  ['E', 'Steuerfrei (E)'],
];

export const taxCategoryLabel = (code) => (TAX_CATEGORIES.find(([c]) => c === code) || [code, code || '–'])[1];

export const ADDRESS_SCHEMES = [
  ['EM', 'E-Mail (EM)'],
  ['0204', 'Leitweg-ID (0204) – Behörden'],
  ['0088', 'GLN (0088)'],
];

// Kleine Kennzeichnung der Normfelder, z. B. „BT-49"
export function BtTag({ code }) {
  return (
    <span className="ml-2 inline-block rounded px-1.5 py-0.5 font-mono text-[10.5px] leading-none bg-muted text-muted-foreground align-middle">
      {code}
    </span>
  );
}

// Herkunft eines Werts: eigener Wert dieser Ebene oder geerbt
export function OriginBadge({ own, ownLabel = 'eigener Wert', inheritLabel = 'Standard' }) {
  return own ? (
    <span className="ml-2 inline-block rounded px-1.5 py-0.5 text-[11px] leading-none bg-primary/10 text-primary align-middle">{ownLabel}</span>
  ) : (
    <span className="ml-2 inline-block rounded px-1.5 py-0.5 text-[11px] leading-none bg-muted text-muted-foreground align-middle">{inheritLabel}</span>
  );
}

// Länderauswahl. inheritLabel gesetzt = es gibt die Option „erben" (Wert null)
export function CountrySelect({ value, onChange, inheritLabel, testId }) {
  const current = value || (inheritLabel ? 'inherit' : 'DE');
  return (
    <Select value={current} onValueChange={(v) => onChange(v === 'inherit' ? null : v)}>
      <SelectTrigger data-testid={testId} className="bg-input border-border text-foreground">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-card border-border max-h-72">
        {inheritLabel && <SelectItem value="inherit">{inheritLabel}</SelectItem>}
        {COUNTRIES.map(([code, name]) => (
          <SelectItem key={code} value={code}>{name} ({code})</SelectItem>
        ))}
        {value && !COUNTRIES.some(([c]) => c === value) && (
          <SelectItem value={value}>{value}</SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}

// Ampel: Prüfung des gespeicherten Kunden gegen EN 16931 (GET /customers/{id}/einvoice-check)
export function CustomerEInvoiceStatus({ api, customerId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = async () => {
    if (!customerId) return;
    setLoading(true);
    setFailed(false);
    try {
      const res = await api.get(`/customers/${customerId}/einvoice-check`);
      setReport(res.data);
    } catch (e) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  if (!customerId) {
    return (
      <div className="rounded-xl border border-border p-4 text-sm text-muted-foreground">
        Die Prüfung für die E-Rechnung erscheint nach dem ersten Speichern.
      </div>
    );
  }
  if (failed) {
    return (
      <div className="rounded-xl border border-border p-4 text-sm text-muted-foreground">
        Prüfung zurzeit nicht abrufbar.
      </div>
    );
  }
  if (!report) {
    return (
      <div className="rounded-xl border border-border p-4 text-sm text-muted-foreground">Prüfe …</div>
    );
  }

  const problems = (report.findings || []).filter((f) => f.severity !== 'hinweis');
  const hints = (report.findings || []).filter((f) => f.severity === 'hinweis');
  const buyer = report.buyer || {};
  const addr = buyer['BG-8'] || {};
  const header = report.header || {};
  const tax = report.tax || {};
  const rows = [
    ['BT-44', buyer['BT-44']],
    ['BT-46', buyer['BT-46']],
    ['BG-8', [addr['BT-50'], addr['BT-51'], [addr['BT-53'], addr['BT-52']].filter(Boolean).join(' '), addr['BT-55']].filter(Boolean).join(', ')],
    ['BT-48', buyer['BT-48']],
    ['BT-49', buyer['BT-49'] ? `${buyer['BT-49'].value} (${buyer['BT-49'].scheme})` : null],
    ['BT-10', header['BT-10']],
    ['BT-19', header['BT-19']],
    ['BT-12', header['BT-12']],
    ['BT-13', header['BT-13']],
    ['BT-151', tax['BT-151'] ? `${tax['BT-151']}${tax['BT-152'] != null ? ` · ${tax['BT-152']} %` : ''}` : null],
    ['BT-84', report.bank ? `${report.bank.label} · ${report.bank.iban_display}` : null],
    ['BT-9', `Rechnungsdatum + ${report.payment_term_days} Tage`],
  ].filter(([, v]) => v);

  const Icon = report.errors > 0 ? XCircle : problems.length > 0 ? AlertTriangle : CheckCircle2;
  const tone = report.errors > 0
    ? 'text-red-600 bg-red-500/10'
    : problems.length > 0 ? 'text-amber-600 bg-amber-500/10' : 'text-emerald-600 bg-emerald-500/10';
  const title = report.errors > 0 ? 'E-Rechnung noch nicht möglich' : 'E-Rechnung möglich';

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-border p-4 space-y-3 bg-card">
        <div className="flex items-start gap-3">
          <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center flex-shrink-0 ${tone}`}>
            <Icon className="w-4 h-4" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-foreground text-sm">{title}</p>
            <p className="text-xs text-muted-foreground">
              Normprüfung EN 16931 · {report.errors} Fehler · {report.warnings} Warnungen
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            aria-label="Neu prüfen"
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {problems.length > 0 && (
          <ul className="space-y-1.5 text-xs">
            {problems.map((f, i) => (
              <li key={i} className={f.severity === 'fehler' ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}>
                {f.message}
              </li>
            ))}
          </ul>
        )}
        {hints.length > 0 && (
          <ul className="space-y-1 text-xs text-muted-foreground">
            {hints.map((f, i) => <li key={i}>{f.message}</li>)}
          </ul>
        )}
        <p className="text-[11px] text-muted-foreground">Geprüft wird der gespeicherte Stand.</p>
      </div>
      <div className="rounded-xl border border-border p-4 space-y-2 bg-muted/30">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">So steht der Kunde in der E-Rechnung</p>
        <div className="grid grid-cols-[56px_1fr] gap-x-2 gap-y-1.5 text-xs">
          {rows.map(([k, v]) => (
            <div key={k} className="contents">
              <span className="font-mono text-[11px] text-muted-foreground">{k}</span>
              <span className="text-foreground break-words">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
